// Pure helpers for the pm-entity-name-needed decide flow (Grant's PM
// ruling, 2026-07-11). The recommendation says "type the entity name —
// creates it and moves the loans", so the rail renders a TEXT input and
// sends params.entity_name on verify; the backend creates the business
// entity, attaches the investor's Private Money loans, links the person
// as principal, and sets Mike Zins as the entity's advisor. The escape
// hatch ("Not known — keep under the personal name") posts keep-current,
// which records the durable business_entity_unknown marker so nobody is
// asked again.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ —
// same convention as expiration-issue.ts / client-since-issue.ts.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface PmEntityIssueLike {
  detectorSlug: string;
}

// Issues whose "verify" carries an entity name to the backend (and whose
// rail block renders the name input + the "not known" escape button).
export function isPmEntityIssue(issue: PmEntityIssueLike): boolean {
  return issue.detectorSlug === "pm-entity-name-needed";
}

// Typing the name IS the action — the backend 400s without it. Mirrors
// expirationDateRequired for ins-missing-expiration.
export function entityNameRequired(issue: PmEntityIssueLike): boolean {
  return isPmEntityIssue(issue);
}

// Client-side floor matching the backend's (>= 3 chars after trim). The
// backend remains the source of truth; this only enables/disables Apply.
export function isValidEntityNameInput(s: string): boolean {
  return s.trim().length >= 3;
}

// Plain-language before→after for the entity-creating verify (UX rule
// #3) — replaces the generic verify copy when this slug is active.
export function pmEntityConsequence(
  issue: PmEntityIssueLike,
  name: string,
): string | null {
  if (!isPmEntityIssue(issue)) return null;
  const trimmed = name.trim();
  if (isValidEntityNameInput(trimmed)) {
    return `Creates the business entity “${trimmed}” (or reuses it if it already exists), moves every un-attached Private Money loan of this investor onto it, links the person as its principal, and sets Mike Zins as the entity's advisor. The personal household keeps its own mortgage advisor for any traditional loans. Reversible for 60 seconds.`;
  }
  return "Type the business entity's name (e.g. “Sunrise Holdings LLC”) — creating it and moving the loans is the whole action here. If it isn't known, use “Not known — keep under the personal name” below.";
}

// Two terminal "keep on the personal household" answers, both posting
// verb=keep-current (the backend distinguishes them by params.keep_reason)
// and both durable + reversible:
//   • 'not_known' — the entity exists but its name isn't known yet.
//   • 'individual_business_purpose' — the loan is CORRECTLY in the
//     individual's name for a business purpose; there's no entity (Grant's
//     2026-07-23 doctrine relaxation for Mike: PM defaults to a business
//     entity, but individual-name-for-business is a valid terminal state).

export type PmEntityKeepReason = "not_known" | "individual_business_purpose";

// Copy for the "not known" escape button + its consequence line.
export const PM_ENTITY_KEEP_PERSONAL_LABEL =
  "Not known — keep under the personal name";

export function pmEntityKeepPersonalConsequence(): string {
  return "Records that no business entity is known for these Private Money loans — they stay under the personal name, the item resolves for the whole team, and it will not be asked again. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}

// Copy for the affirmative "individual's name, business purpose" answer.
export const PM_ENTITY_INDIVIDUAL_LABEL =
  "Held in the individual's name (business purpose)";

export function pmEntityIndividualConsequence(): string {
  return "Records that these Private Money loans are correctly held in the individual's name for a business purpose — there is no separate business entity. They stay under the personal name, the item resolves for the whole team, and it will not be asked again. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}
